import { useCallback, useEffect, useMemo, useState } from "react";
import { verifyLicense, LICENSE_PUBLIC_KEY, type VerifyResult } from "../core/index.ts";
import { getPairId, normalizePairId } from "./pairing.ts";
import { activate as apiActivate, check as apiCheck } from "./licenseApi.ts";

const STORAGE_KEY = "carspend_license";
const GRACE_KEY = "carspend_license_lastcheck";
// If the registry is unreachable, keep working for this long since the last
// successful check (covers transient outages without leaking a revoked code).
const GRACE_SECONDS = 72 * 60 * 60;

/** A code is valid for THIS browser only if its `sub` matches our pairing id. */
function boundToThisBrowser(result: VerifyResult, pairId: string): boolean {
  const sub = result.payload?.sub;
  return sub != null && normalizePairId(sub) === normalizePairId(pairId);
}

function withinGrace(): boolean {
  const t = Number(localStorage.getItem(GRACE_KEY) ?? 0);
  return t > 0 && (Date.now() / 1000 - t) < GRACE_SECONDS;
}
function markChecked() { localStorage.setItem(GRACE_KEY, String(Math.floor(Date.now() / 1000))); }

/**
 * Pro entitlement for the web app. A signed unlock code (issued by the phone app
 * while Pro is active) is stored locally and verified offline against the bundled
 * public key. No accounts, no server.
 */
export function useEntitlement() {
  const [isPro, setIsPro] = useState(false);
  const [checked, setChecked] = useState(false);
  const [expiry, setExpiry] = useState<number | null>(null);
  const pairId = useMemo(() => getPairId(), []);

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem(STORAGE_KEY);
      if (token) {
        const r = await verifyLicense(token, LICENSE_PUBLIC_KEY);
        const localOk = r.ok && boundToThisBrowser(r, pairId);
        if (localOk) {
          // Registry check: is this still the single active browser for the purchase?
          const c = await apiCheck(token);
          if (!c.enabled) { setIsPro(true); setExpiry(r.payload?.exp ?? null); }
          else if (c.reachable) {
            if (c.active) { markChecked(); setIsPro(true); setExpiry(r.payload?.exp ?? null); }
            else { localStorage.removeItem(STORAGE_KEY); localStorage.removeItem(GRACE_KEY); } // revoked elsewhere
          } else if (withinGrace()) { setIsPro(true); setExpiry(r.payload?.exp ?? null); } // transient outage
          // else: unreachable and out of grace → stay locked until a check succeeds
        } else if (r.reason === "expired" || r.ok) {
          localStorage.removeItem(STORAGE_KEY); // expired or bound to a different browser
        }
      }
      setChecked(true);
    })();
  }, [pairId]);

  /** Redeem an unlock code. Returns the verify result; stores it on success. */
  const redeem = useCallback(async (code: string): Promise<VerifyResult> => {
    const r = await verifyLicense(code, LICENSE_PUBLIC_KEY);
    if (!r.ok) return r;
    // Signature/claims are fine, but the code must be bound to THIS browser.
    if (!boundToThisBrowser(r, pairId)) return { ok: false, reason: "device", payload: r.payload };
    const token = code.trim();
    // Claim the single active-browser slot (kicks any previously-active browser).
    const a = await apiActivate(token);
    if (a.enabled) markChecked();
    localStorage.setItem(STORAGE_KEY, token);
    setIsPro(true);
    setExpiry(r.payload?.exp ?? null);
    return { ok: true, payload: r.payload };
  }, [pairId]);

  const clear = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(GRACE_KEY);
    setIsPro(false);
    setExpiry(null);
  }, []);

  return { isPro, checked, expiry, redeem, clear, pairId };
}
